import { API, HOST } from '@/utils/http'

// Per org, only the newest request may write the view: a refresh or a role write that
// lands while an older /access request is in flight must not be overwritten by it.
const seq = {}
const latest = {}
const pending = {}

const bump = orgId => (seq[orgId] = (seq[orgId] ?? 0) + 1)

const load = (commit, orgId) => {
  const n = bump(orgId)
  const request = API.get(`${HOST}/organization/${orgId}/access`)
    .then(({ data }) => {
      if (seq[orgId] === n) commit('SET_VIEW', { orgId, view: data })
      else if (latest[orgId] !== request) return latest[orgId]
    })
    .finally(() => {
      if (pending[orgId] === request) delete pending[orgId]
    })
  latest[orgId] = request
  pending[orgId] = request
  return request
}

const rolesUrl = (state, path = '') => `${HOST}/organization/${state.orgId}/roles${path}`
const orgUrl = (state, path) => `${HOST}/organization/${state.orgId}${path}`
const isMe = (rootGetters, memberId) => rootGetters['user/getMember']?.member_id === memberId

export const actions = {
  fetch({ commit }, orgId) {
    orgId = Number(orgId)
    if (!orgId) return Promise.resolve()
    commit('SET_ORG', orgId)
    return load(commit, orgId)
  },

  ensure({ state, commit }, orgId) {
    orgId = Number(orgId)
    if (!orgId) return Promise.resolve()
    commit('SET_ORG', orgId)
    if (state.views[orgId]) return Promise.resolve()
    return pending[orgId] ?? load(commit, orgId)
  },

  refresh({ commit }, orgId) {
    orgId = Number(orgId)
    return orgId ? load(commit, orgId) : Promise.resolve()
  },

  onWsEvent({ state, dispatch }, { event, data }) {
    const orgId = Number(data?.organization_id)
    if (event !== 'roles_changed' || !state.views[orgId]) return
    return dispatch('refresh', orgId).catch(() => {})
  },

  async createRole({ state, commit }, { kind, name, description, copyFromRoleId }) {
    const body = { kind, name, description }
    if (copyFromRoleId) body.copy_from_role_id = copyFromRoleId
    const { data } = await API.post(rolesUrl(state), body)
    bump(state.orgId)
    commit('SET_ROLE', data)
    return data
  },

  async updateRole({ state, commit }, { roleId, ...patch }) {
    const { data } = await API.patch(rolesUrl(state, `/${roleId}`), patch)
    bump(state.orgId)
    commit('SET_ROLE', data)
    return data
  },

  async resetRole({ state, commit }, { roleId, domain }) {
    const { data } = await API.post(rolesUrl(state, `/${roleId}/reset`), null, { params: domain ? { domain } : {} })
    bump(state.orgId)
    commit('SET_ROLE', data)
    return data
  },

  async deleteRole({ state, commit }, { roleId, reassignTo }) {
    await API.delete(rolesUrl(state, `/${roleId}`), { params: reassignTo ? { reassign_to: reassignTo } : {} })
    bump(state.orgId)
    commit('REMOVE_ROLE', roleId)
    if (reassignTo) await load(commit, state.orgId)
  },

  async setDefaultJoin({ state, commit }, roleId) {
    const { data } = await API.put(rolesUrl(state, `/${roleId}/default-join`))
    bump(state.orgId)
    commit('SET_DEFAULT_JOIN', data)
    commit('SET_ROLE', data)
    return data
  },

  async assignOrgRole({ state, commit, rootGetters }, { memberId, roleId }) {
    await API.put(orgUrl(state, `/members/${memberId}/role`), { role_id: roleId })
    if (isMe(rootGetters, memberId)) await load(commit, state.orgId)
  },

  async assignProjectRole({ state, commit, rootGetters }, { projectId, memberId, roleId }) {
    await API.put(orgUrl(state, `/project/${projectId}/members/${memberId}/role`), { role_id: roleId })
    if (isMe(rootGetters, memberId)) await load(commit, state.orgId)
  },

  async transferOwner({ state, commit }, memberId) {
    await API.post(orgUrl(state, '/owner/transfer'), { member_id: memberId })
    await load(commit, state.orgId)
  },
}
